import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, {
  IBrandingApi,
  IColorScheme,
} from '@services/BrandingsService';
import EventsService from '@services/EventsService';
import { usePreventGameKeydownListening } from '@hooks';

import { useEditorMode } from '../EditorModeContext';
import { buildBrandingSwitchEntities } from '../brandingEntities';
import { sendBrandingEntitiesUpdatedToUnreal } from '../brandingSpotsUnreal';
import { useBrandingBarStyles } from './styles';

interface QuickSwitcherProps {
  anchorEl: HTMLElement | null;
  onCreateNew?: () => void;
  onOpenLibrary?: () => void;
}

const renderSwatch = (
  colors: IColorScheme | undefined,
  classes: ReturnType<typeof useBrandingBarStyles>,
) => {
  const has =
    colors?.primaryColor || colors?.secondaryColor || colors?.outlineColor;
  if (!has) {
    return (
      <Box sx={classes.brandSwatchRow}>
        <Box sx={classes.brandSwatchPlaceholder} />
      </Box>
    );
  }
  return (
    <Box sx={classes.brandSwatchRow}>
      <Box
        sx={[
          classes.brandSwatchStripe,
          {
            backgroundColor: colors?.primaryColor || 'rgba(255,255,255,0.1)',
          },
        ]}
      />
      <Box
        sx={[
          classes.brandSwatchStripe,
          {
            backgroundColor: colors?.secondaryColor || 'rgba(255,255,255,0.1)',
          },
        ]}
      />
      <Box
        sx={[
          classes.brandSwatchStripe,
          {
            backgroundColor: colors?.outlineColor || 'rgba(255,255,255,0.1)',
          },
        ]}
      />
    </Box>
  );
};

const QuickSwitcher = ({
  anchorEl,
  onCreateNew,
  onOpenLibrary,
}: QuickSwitcherProps) => {
  const { t: translate } = useTranslation();
  const classes = useBrandingBarStyles();

  const { isQuickSwitcherOpen, closeQuickSwitcher, setAutosaveStatus } =
    useEditorMode();
  const {
    currentRoom,
    setCurrentRoom,
    currentBrandingId,
    setCurrentBrandingId,
    setBrandingSpots,
    setRoomSpots,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();

  const mapId = currentRoom?.map?.mapId;
  const activeBrandingId = currentRoom?.activeBrandingId;
  const roomId = currentRoom?.roomId;

  const searchInputRef = usePreventGameKeydownListening<HTMLInputElement>();

  const [search, setSearch] = useState('');
  const [brandings, setBrandings] = useState<IBrandingApi[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    if (!isQuickSwitcherOpen || !mapId) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    BrandingsService.getBrandings({ mapId, limit: 100 })
      .then((res) => {
        if (!cancelled) {
          setBrandings(res.brandings ?? []);
        }
      })
      .catch((loadError) => {
        console.error(
          `Failed to load brandings for quick switcher: ${
            (loadError as Error)?.message
          }`,
          { mapId, brandingId: currentBrandingId },
        );
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isQuickSwitcherOpen, mapId, currentBrandingId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return brandings;
    }
    return brandings.filter((b) =>
      (b.name || 'Unnamed').toLowerCase().includes(q),
    );
  }, [brandings, search]);

  const handleSelectAndActivate = async (brandingId: string) => {
    if (!brandingId || !roomId || isSwitching) {
      return;
    }
    setIsSwitching(true);
    setAutosaveStatus('saving');
    // Read before the state update below, since the union payload needs the
    // configuration being left.
    const previousBrandingId = currentBrandingId;
    try {
      setCurrentBrandingId(brandingId);
      sendMessageToUnreal({ action: 'CLEAR_SPOTS' });
      sendMessageToUnreal({ action: 'ENTER_TO_CUSTOMIZATION_MODE' });
      await BrandingsService.activateBranding(brandingId, roomId);
      setCurrentRoom((prev) =>
        prev && prev.roomId === roomId
          ? { ...prev, activeBrandingId: brandingId }
          : prev,
      );
      sendMessageToUnreal({
        action: 'BRANDING_ACTIVATED',
        brandingId,
        roomId,
      });

      // Per-entity replace cannot unset an entity absent from the payload, so
      // the switch sends the union of both configurations: the new values, plus
      // an empty map for every entity the new one drops, which restores what the
      // level authored. One message, one rebuild per object — a CLEAR_BRANDING
      // first would rebuild everything twice.
      sendBrandingEntitiesUpdatedToUnreal(
        sendMessageToUnreal,
        buildBrandingSwitchEntities(
          brandings.find(
            (branding) => branding.brandingId === previousBrandingId,
          )?.brandingEntities,
          brandings.find((branding) => branding.brandingId === brandingId)
            ?.brandingEntities,
        ),
      );
      const [brandingSpotsRes, roomSpotsRes] = await Promise.all([
        BrandingsService.getBrandingSpots(brandingId, { limit: 200 }),
        EventsService.getRoomSpots(roomId),
      ]);
      setBrandingSpots(brandingSpotsRes.spots ?? []);
      setRoomSpots(roomSpotsRes);
      setAutosaveStatus('saved');
      closeQuickSwitcher();
    } catch (selectError) {
      console.error(
        `Failed to select+activate branding: ${
          (selectError as Error)?.message
        }`,
        { brandingId },
      );
      setAutosaveStatus('error');
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <Popover
      open={isQuickSwitcherOpen}
      anchorEl={anchorEl}
      onClose={closeQuickSwitcher}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      PaperProps={{ sx: classes.quickSwitcherPaper }}
    >
      <Box sx={classes.quickSearchWrap}>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder={translate('editorMode.quickSearchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          onKeyUp={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          sx={classes.quickSearchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'data-testid': 'quick-switcher-search-input' }}
        />
      </Box>

      <Box sx={classes.quickList}>
        {isLoading || isSwitching ? (
          <Box sx={classes.quickLoader}>
            <CircularProgress size={18} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography sx={classes.quickEmpty}>
            {translate('editorMode.quickNoResults')}
          </Typography>
        ) : (
          filtered.map((b) => {
            const id = b.brandingId;
            const isEditing = id === currentBrandingId;
            const isLive = !!activeBrandingId && id === activeBrandingId;
            return (
              <Tooltip
                key={id}
                title={
                  isLive
                    ? translate('editorMode.liveTooltip')
                    : translate('editorMode.activateOnSelect')
                }
                placement="left"
                enterDelay={400}
              >
                <Box
                  sx={[classes.quickRow, isEditing && classes.quickRowActive]}
                  onClick={() => !isLive && handleSelectAndActivate(id)}
                  data-testid={`quick-switcher-row-${id}`}
                >
                  {renderSwatch(b.brandingColors, classes)}
                  <Typography sx={classes.quickName}>
                    {b.name || translate('adminToolbox.unnamed')}
                  </Typography>
                  <Box sx={classes.quickStatusSlot}>
                    {isLive ? (
                      <RadioButtonCheckedIcon
                        sx={classes.quickStatusLiveIcon}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={classes.quickStatusInactiveIcon}
                      />
                    )}
                  </Box>
                </Box>
              </Tooltip>
            );
          })
        )}
      </Box>

      <Box sx={classes.quickFooter}>
        <Button
          variant="outlined"
          sx={classes.quickFooterButton}
          onClick={() => {
            closeQuickSwitcher();
            onCreateNew?.();
          }}
          data-testid="quick-switcher-create-btn"
        >
          {translate('editorMode.quickCreate')}
        </Button>
        <Button
          variant="outlined"
          sx={classes.quickFooterButton}
          onClick={() => {
            closeQuickSwitcher();
            onOpenLibrary?.();
          }}
          data-testid="quick-switcher-library-btn"
        >
          {translate('editorMode.quickAllBrandings')}
        </Button>
      </Box>
    </Popover>
  );
};

export default QuickSwitcher;
